import { Suspense, lazy, memo, useEffect, useMemo, useState } from 'react';
import { type RouteObject, createBrowserRouter } from 'react-router';
import { DebugLevel } from '../../_configs/types';
import { useAppSelector } from '../../_flux/Store';
import { useAllFaarenVehiclesContext } from '../../context/all-faaren-vehicles';
import { useAllFacetsContext } from '../../context/all-facets';
import { useModuleSkeletonContext } from '../../context/module-skeleton';
import { useSettingsContext } from '../../context/settings';
import checkUndefinedNullOrEmpty from '../../utils/tools/checkUndefinedNullOrEmpty';
import checkUndefinedOrNull from '../../utils/tools/checkUndefinedOrNull';
import ErrorBoundary from './error';
import filterMap from './filter/maps/filterMap';
import checkOldUrls from './helpers/checkOldUrls';
import { fahrzeugsucheModuleName } from './types';

const FahrzeugDetails = lazy(() => import('./details' /* webpackChunkName: "FahrzeugDetails" */));
const FahrzeugsucheList = lazy(() => import('./list' /* webpackChunkName: "FahrzeugsucheList" */));
const FahrzeugsucheProvider = lazy(() => import('./provider' /* webpackChunkName: "FahrzeugsucheProvider" */));
const RouterProvider = lazy(() => import('react-router/dom' /* webpackChunkName: "RouterProvider" */).then((module) => ({ default: module?.RouterProvider })));

let fahrzeugsucheTimerAutoUncle: NodeJS.Timeout;

function Fahrzeugsuche() {
    const allFacets = useAllFacetsContext();
    const allFaarenVehicles = useAllFaarenVehiclesContext();
    const settings = useSettingsContext();
    const Skeleton = useModuleSkeletonContext();
    const [loading, setLoading] = useState<boolean>(true);
    const viewOptions = useAppSelector((store) => store?.[fahrzeugsucheModuleName]?.viewOptions);

    const filterElements = useMemo(() => filterMap(settings?.general?.useFirmenFilter), [settings?.general?.useFirmenFilter]);

    async function oldchecker() {
        if (loading === true && settings?.general?.useOldRouter === false) {
            await checkOldUrls(location, allFacets, filterElements, settings);
            setLoading(false);
        }
    }

    useEffect(() => {
        oldchecker();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // AutoUncle script for embedding TradeIn modal in details page
    useEffect(() => {
        if (settings?.general?.debug === DebugLevel.EXTERNALS || settings?.general?.debug >= DebugLevel.INFO) {
            console.log('Setting up AutoUncle TradeIn Script...');
        }

        const auCwebScript = document.getElementById('au-cweb-script');

        if (!checkUndefinedOrNull(auCwebScript)) {
            auCwebScript.remove();
        }

        if (settings?.api?.autoUncle?.tradeIn && !checkUndefinedNullOrEmpty(settings?.api?.autoUncle?.id)) {
            clearTimeout(fahrzeugsucheTimerAutoUncle);
            fahrzeugsucheTimerAutoUncle = setTimeout(() => {
                window.AuEmbed = {
                    ...(window?.AuEmbed || {}),
                    config: {
                        ...(window?.AuEmbed?.config || {}),
                        id: settings?.api?.autoUncle?.id,
                        disableGoogleAnalytics: true,
                        departmentId: undefined,
                        data: {},
                        noDelayedCtaPage: true,
                        previewUrl: window.location.href
                    },
                    pushedUrls: [],
                    pushUrl: function (url: string) {
                        window.AuEmbed?.pushedUrls?.push(url);
                    },
                };

                // Ensure viewport meta exists, necessary if script is added to <head> instead of <body>!
                if (!document?.head?.querySelector("meta[name=viewport]")) {
                    console.warn('AutoUncleEmbedProvider - Missing <meta name="viewport"> tag!');
                }

                const d = {
                    depId: window?.AuEmbed?.config?.departmentId,
                    device: window.matchMedia('only screen and (max-width: 760px)').matches ? 'mobile' : 'desktop',
                    pageType: window?.AuEmbed?.config?.pageType,
                    url: window?.AuEmbed?.config?.previewUrl || document.location.href,
                };

                const s = document.createElement('script');
                s.async = !0;
                s.src = `https://www.autouncle.de/de/customers/${encodeURIComponent(window?.AuEmbed?.config?.id)}/website_script.js?d=${encodeURIComponent(JSON.stringify(d))}`;
                s.id = 'au-cweb-script';

                const h = document.getElementsByTagName('head')[0];
                h.insertBefore(s, h.lastChild);

                if (!checkUndefinedOrNull(window?.auws?.reload)) {
                    window.auws.reload();
                }
                clearTimeout(fahrzeugsucheTimerAutoUncle);
            }, 2000);
        }

        return function cleanup() {
            clearTimeout(fahrzeugsucheTimerAutoUncle);
            const auCwebScriptRemove = document.getElementById('au-cweb-script');

            if (!checkUndefinedOrNull(window?.auws?.unload)) {
                window.auws.unload();
            }

            if (!checkUndefinedOrNull(auCwebScriptRemove)) {
                auCwebScriptRemove.remove();
            }
        };
    }, [settings?.api?.autoUncle?.departmentIds, settings?.api?.autoUncle?.id, settings?.api?.autoUncle?.tradeIn, settings?.general?.debug, viewOptions?.car]);

    const detail = (
        <Suspense fallback={Skeleton.details}>
            <FahrzeugsucheProvider>
                <FahrzeugDetails allFaarenVehicles={allFaarenVehicles} module={fahrzeugsucheModuleName} />
            </FahrzeugsucheProvider>
        </Suspense>
    );
    const list = (
        <Suspense fallback={Skeleton.module}>
            <FahrzeugsucheProvider>
                <FahrzeugsucheList allFaarenVehicles={allFaarenVehicles} filterElements={filterElements} />
            </FahrzeugsucheProvider>
        </Suspense>
    );

    const loader = useMemo(() => location?.pathname?.includes('/c/') ? Skeleton.details : Skeleton.module, [Skeleton.details, Skeleton.module]);

    if (settings?.general?.useOldRouter === true) {
        return !checkUndefinedNullOrEmpty(viewOptions?.car) ? detail : list;
    }

    const commonRouteSettings: RouteObject = {
        errorElement: <ErrorBoundary module={fahrzeugsucheModuleName} settings={settings} />,
    };

    const fahrzeugsucheIsOnRootPage = checkUndefinedNullOrEmpty(settings?.general?.searchPath?.replace(/^\//g, ''));
    const fahrzeugsucheRouterChildren = [
        {
            ...commonRouteSettings,
            path: 'c/:carDesign/:carId',
            element: detail,
            hydrateFallbackElement: Skeleton.details,
            loader: () => Skeleton.details,
        },
        {
            ...commonRouteSettings,
            path: ':hersteller?/:modell?/:typ?/:standort?',
            element: list,
            hydrateFallbackElement: Skeleton.module,
            loader: () => Skeleton.module,
        },
    ];

    const router = createBrowserRouter([
        {
            ...commonRouteSettings,
            path: '/',
            children: [
                ...fahrzeugsucheRouterChildren,
                {
                    ...commonRouteSettings,
                    path: fahrzeugsucheIsOnRootPage ? '/' : settings?.general?.searchPath?.replace(/^\//g, ''),
                    children: fahrzeugsucheRouterChildren,
                },
                {
                    ...commonRouteSettings,
                    path: fahrzeugsucheIsOnRootPage ? 'v1' : `v1/${settings?.general?.searchPath?.replace(/^\//g, '')}`,
                    children: fahrzeugsucheRouterChildren,
                },
                {
                    ...commonRouteSettings,
                    path: fahrzeugsucheIsOnRootPage ? 'v2' : `v2/${settings?.general?.searchPath?.replace(/^\//g, '')}`,
                    children: fahrzeugsucheRouterChildren,
                },
            ],
        },
    ]);

    return <Suspense fallback={loader}>
        {loading ? loader : <RouterProvider router={router} />}
    </Suspense>;
}

export default memo(Fahrzeugsuche);
